/**
 * Frontend Log Capture Service
 * Captures all console logs and sends them to the backend with session context
 * for real-time debugging and audit trail purposes
 */

class FrontendLogCapture {
    constructor() {
        this.sessionId = null;
        this.customerId = null;
        this.customerData = null;
        this.isEnabled = true;
        this.originalConsole = {};
        this.logBuffer = [];
        this.maxBufferSize = 20; // Further reduced from 50
        this.batchSize = 3; // Further reduced from 5
        this.batchTimeout = 5000; // Increased from 3000
        this.batchTimer = null;

        // Rate limiting to prevent server overload
        this.lastSendTime = 0;
        this.minSendInterval = 1000; // Minimum 1 second between sends
        this.failedAttempts = 0;
        this.maxFailedAttempts = 5;
        this.backoffDelay = 5000; // 5 second delay after max failures

        // Store original console methods
        this.originalConsole = {
            log: console.log.bind(console),
            error: console.error.bind(console),
            warn: console.warn.bind(console),
            info: console.info.bind(console),
            debug: console.debug.bind(console)
        };

        this.initializeSessionId();
        this.initializeCustomerData();
        this.setupConsoleInterception();
        this.setupErrorHandling();
        this.setupUnloadHandler();

        console.log(`📦 Frontend log capture initialized for session: ${this.sessionId}`);
    }

    initializeSessionId() {
        // Try to get existing session ID from various sources
        this.sessionId =
            sessionStorage.getItem('sessionId') ||
            localStorage.getItem('sessionId') ||
            this.generateSessionId();

        // Store it for consistency
        sessionStorage.setItem('sessionId', this.sessionId);

        // Also set it globally for other parts of the app to use
        window.currentSessionId = this.sessionId;
    }

    initializeCustomerData() {
        // Try to get existing customer data from session storage
        try {
            const customerId = sessionStorage.getItem('customerId');
            const customerData = sessionStorage.getItem('customerData');

            if (customerId) {
                this.customerId = customerId;
            }

            if (customerData) {
                this.customerData = JSON.parse(customerData);
            }
        } catch (error) {
            console.error('Error loading customer data:', error);
        }
    }

    generateSessionId() {
        const timestamp = Date.now();
        const randomPart = Math.random().toString(36).substr(2, 12);
        return `session_${timestamp}_${randomPart}`;
    }

    setupConsoleInterception() {
        // Override console methods to capture logs
        console.log = (...args) => {
            // Filter out debug logs to avoid spam
            const message = args.join(' ');
            const isDebugLog = message.includes('🔍 DEBUG:') ||
                message.includes('📅') ||
                message.includes('✅ Fetched') ||
                message.includes('🔄 BookingCalendar') ||
                message.includes('🎯 URL OVERRIDE:') ||
                message.includes('slots');

            if (!isDebugLog) {
                this.captureLog('info', args, 'console.log');
            }
            this.originalConsole.log(...args);
        };

        console.error = (...args) => {
            this.captureLog('error', args, 'console.error');
            this.originalConsole.error(...args);
        };

        console.warn = (...args) => {
            this.captureLog('warn', args, 'console.warn');
            this.originalConsole.warn(...args);
        };

        console.info = (...args) => {
            this.captureLog('info', args, 'console.info');
            this.originalConsole.info(...args);
        };

        console.debug = (...args) => {
            this.captureLog('debug', args, 'console.debug');
            this.originalConsole.debug(...args);
        };
    }

    setupErrorHandling() {
        // Capture unhandled errors
        window.addEventListener('error', (event) => {
            this.captureLog('error', [event.message], 'unhandled_error', {
                filename: event.filename,
                lineno: event.lineno,
                colno: event.colno,
                stack: event.error?.stack
            });
        });

        // Capture unhandled promise rejections
        window.addEventListener('unhandledrejection', (event) => {
            this.captureLog('error', [event.reason], 'unhandled_rejection', {
                stack: event.reason?.stack
            });
        });
    }

    setupUnloadHandler() {
        // Send any remaining logs when user leaves the page
        window.addEventListener('beforeunload', () => {
            this.flushLogs(true); // Synchronous send
        });

        window.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'hidden') {
                this.flushLogs();
            }
        });
    }

    captureLog(level, args, source, metadata = {}) {
        if (!this.isEnabled) return;

        // Filter out noisy logs from third-party libraries
        const message = this.formatLogArgs(args);
        if (this.shouldFilterLog(message, source)) {
            return;
        }

        // Safely serialize the data
        let safeData;
        try {
            const rawData = args.length === 1 && typeof args[0] === 'object' ? args[0] : args;
            safeData = this.safeStringify(rawData);
        } catch (e) {
            safeData = { error: 'Failed to serialize log data', args: args.map(arg => String(arg)) };
        }

        // Safely serialize metadata
        let safeMetadata;
        try {
            safeMetadata = this.safeStringify({
                ...metadata,
                viewport: {
                    width: window.innerWidth,
                    height: window.innerHeight
                }
            });
        } catch (e) {
            safeMetadata = { error: 'Failed to serialize metadata' };
        }

        const logEntry = {
            id: `log_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            timestamp: new Date().toISOString(),
            level,
            source: 'frontend',
            category: source,
            message: message,
            data: safeData,
            sessionId: this.sessionId,
            customerId: this.customerId,
            customerName: this.customerData ? `${this.customerData.firstName || ''} ${this.customerData.lastName || ''}`.trim() : null,
            page: window.location.pathname,
            url: window.location.href,
            userAgent: navigator.userAgent,
            metadata: {
                ...safeMetadata,
                ...(this.customerData && {
                    customerEmail: this.customerData.email,
                    customerPhone: this.customerData.phone
                })
            }
        };

        // Add to buffer
        this.logBuffer.push(logEntry);

        // Manage buffer size
        if (this.logBuffer.length > this.maxBufferSize) {
            this.logBuffer = this.logBuffer.slice(-this.maxBufferSize);
        }

        // Trigger batch send if buffer is full or it's an error
        if (this.logBuffer.length >= this.batchSize || level === 'error') {
            this.flushLogs();
        } else {
            this.scheduleBatchSend();
        }
    }

    // Safe JSON serializer that handles circular references
    safeStringify(obj, maxDepth = 5, depth = 0) {
        if (depth > maxDepth) {
            return '[Max Depth Reached]';
        }

        if (obj === null || obj === undefined) {
            return obj;
        }

        if (typeof obj !== 'object') {
            return obj;
        }

        // Handle circular references
        if (this._seenObjects && this._seenObjects.has(obj)) {
            return '[Circular Reference]';
        }

        // Initialize seen objects tracker
        if (!this._seenObjects) {
            this._seenObjects = new WeakSet();
        }
        this._seenObjects.add(obj);

        try {
            if (Array.isArray(obj)) {
                const result = obj.slice(0, 10).map(item => this.safeStringify(item, maxDepth, depth + 1));
                if (obj.length > 10) {
                    result.push(`[... ${obj.length - 10} more items]`);
                }
                return result;
            }

            // Handle DOM elements
            if (obj instanceof Element) {
                return `[${obj.tagName}${obj.id ? '#' + obj.id : ''}${obj.className ? '.' + obj.className.split(' ').join('.') : ''}]`;
            }

            // Handle Error objects
            if (obj instanceof Error) {
                return {
                    name: obj.name,
                    message: obj.message,
                    stack: obj.stack?.split('\n').slice(0, 5).join('\n') // Limit stack trace
                };
            }

            // Handle regular objects
            const result = {};
            let count = 0;
            for (const key in obj) {
                if (count >= 20) { // Reduced limit for better performance
                    result['[...more]'] = `[${Object.keys(obj).length - 20} more properties]`;
                    break;
                }

                if (obj.hasOwnProperty(key)) {
                    // Skip React internals and functions
                    if (key.startsWith('__react') || key.startsWith('_react') || typeof obj[key] === 'function') {
                        continue;
                    }

                    // Special handling for potentially circular booking references
                    if (key === 'actualSlot' && depth > 2) {
                        result[key] = '[Circular Booking Reference]';
                        count++;
                        continue;
                    }

                    try {
                        result[key] = this.safeStringify(obj[key], maxDepth, depth + 1);
                        count++;
                    } catch (e) {
                        result[key] = '[Unserializable]';
                        count++;
                    }
                }
            }
            return result;
        } finally {
            // Clean up for this level
            if (depth === 0) {
                this._seenObjects = null;
            }
        }
    }

    shouldFilterLog(message, source) {
        // Filter out noisy third-party logs
        const filters = [
            'Vercel Speed Insights',
            'HMR',
            'hot reload',
            'webpack',
            'Download the React DevTools',
            'react-dom.development.js',
            'chunk',
            'sockjs',
            'performance.mark',
            'FID:',
            'CLS:',
            'LCP:',
            'TTFB:',
            // Prevent feedback loops from logging system itself
            'Failed to send frontend logs',
            'Failed to track analytics',
            'WebSocket connection failed',
            'WebSocket error',
            'WebSocket connection closed',
            'Connecting to real-time log stream',
            'ERR_INSUFFICIENT_RESOURCES',
            'flushLogs',
            'trackAnalytics',
            'frontend log capture'
        ];

        return filters.some(filter =>
            message.toLowerCase().includes(filter.toLowerCase())
        );
    }

    formatLogArgs(args) {
        return args.map(arg => {
            if (typeof arg === 'object' && arg !== null) {
                try {
                    const safeObj = this.safeStringify(arg);
                    return JSON.stringify(safeObj, null, 2);
                } catch (e) {
                    return `[Object: ${Object.prototype.toString.call(arg)}]`;
                }
            }
            return String(arg);
        }).join(' ');
    }

    scheduleBatchSend() {
        if (this.batchTimer) {
            clearTimeout(this.batchTimer);
        }

        this.batchTimer = setTimeout(() => {
            this.flushLogs();
        }, this.batchTimeout);
    }

    async flushLogs(synchronous = false) {
        if (this.logBuffer.length === 0) return;

        // Rate limiting check
        const now = Date.now();
        if (!synchronous && now - this.lastSendTime < this.minSendInterval) {
            // Too soon, schedule for later
            this.scheduleBatchSend();
            return;
        }

        // Check if we're in backoff period due to failures
        if (this.failedAttempts >= this.maxFailedAttempts &&
            now - this.lastSendTime < this.backoffDelay) {
            return;
        }

        const logsToSend = [...this.logBuffer];
        this.logBuffer = [];

        if (this.batchTimer) {
            clearTimeout(this.batchTimer);
            this.batchTimer = null;
        }

        const payload = {
            sessionId: this.sessionId,
            customerId: this.customerId,
            logs: logsToSend,
            source: 'frontend_batch',
            timestamp: new Date().toISOString()
        };

        try {
            // Safely serialize the payload
            const safePayload = JSON.stringify(payload, (key, value) => {
                // Handle circular references
                if (typeof value === 'object' && value !== null) {
                    if (this._seenInStringify && this._seenInStringify.has(value)) {
                        return '[Circular Reference]';
                    }
                    if (!this._seenInStringify) {
                        this._seenInStringify = new WeakSet();
                    }
                    this._seenInStringify.add(value);
                }
                return value;
            });

            // Clear the seen objects after stringification
            this._seenInStringify = null;

            this.lastSendTime = now;

            if (synchronous) {
                // Use sendBeacon for synchronous sending during page unload
                navigator.sendBeacon('/api/analytics/logs/batch', safePayload);
            } else {
                // Use fetch for regular async sending - NOW USING ANALYTICS API
                await fetch('/api/analytics/logs/batch', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Session-ID': this.sessionId,
                        ...(this.customerId && { 'X-Customer-ID': this.customerId })
                    },
                    body: safePayload
                });
            }

            // Reset failed attempts on success
            this.failedAttempts = 0;
        } catch (error) {
            // Increment failed attempts
            this.failedAttempts++;

            // Only restore logs if we haven't exceeded max attempts
            if (this.failedAttempts < this.maxFailedAttempts) {
                this.logBuffer = [...logsToSend, ...this.logBuffer].slice(-this.maxBufferSize);
            }

            // Use original console to avoid infinite loop - but silently fail if too many attempts
            if (this.failedAttempts <= 2) {
                this.originalConsole.error('Failed to send frontend logs:', error);
            }
        }
    }

    // Public methods for the app to use
    setCustomerId(customerId, customerData = null) {
        this.customerId = customerId;
        sessionStorage.setItem('customerId', customerId);

        // Store customer data for future logs
        if (customerData) {
            this.customerData = customerData;
            sessionStorage.setItem('customerData', JSON.stringify(customerData));
        }

        // Log the customer association
        this.captureLog('info', [`Customer associated with session: ${customerId}`], 'customer_association');
    }

    setSessionContext(context) {
        Object.assign(this, context);
    }

    logUserAction(action, page, data = {}) {
        this.captureLog('info', [`User action: ${action}`], 'user_action', {
            action,
            page: page || window.location.pathname,
            actionData: data
        });
    }

    logPageView(page) {
        this.captureLog('info', [`Page view: ${page}`], 'page_view', {
            page,
            referrer: document.referrer,
            timestamp: new Date().toISOString()
        });
    }

    logError(error, context = {}) {
        this.captureLog('error', [error.message || error], 'app_error', {
            stack: error.stack,
            context
        });
    }

    disable() {
        this.isEnabled = false;
        // Restore original console methods
        Object.assign(console, this.originalConsole);
    }

    enable() {
        this.isEnabled = true;
        this.setupConsoleInterception();
    }

    getSessionId() {
        return this.sessionId;
    }

    getStats() {
        return {
            sessionId: this.sessionId,
            customerId: this.customerId,
            isEnabled: this.isEnabled,
            bufferSize: this.logBuffer.length,
            maxBufferSize: this.maxBufferSize
        };
    }
}

// Create singleton instance
let frontendLogCapture = null;

export function initializeFrontendLogCapture() {
    if (frontendLogCapture) {
        console.warn('Frontend log capture already initialized');
        return frontendLogCapture;
    }

    frontendLogCapture = new FrontendLogCapture();

    // Make it globally available
    window.logCapture = frontendLogCapture;

    return frontendLogCapture;
}

export function getFrontendLogCapture() {
    return frontendLogCapture;
}

export default FrontendLogCapture;